/**
 * A street-race rival at the wheel: follows the race line (pure pursuit on the waypoints), brakes for the
 * corner caps and for anything square in front of the bonnet, overtakes on the crown, backs out when
 * wedged, and — only when nobody can see it — hops one waypoint after being stuck for a long while, so a
 * pile-up behind the player doesn't end the race. It writes `vehicle.controls` like traffic does (order
 * band 200–299) and never moves the body itself, except for that out-of-sight recovery.
 */
import type { GameContext } from '../core/context';
import { angleDelta, clamp, distance, type V2 } from '../core/math';
import { Layer } from '../physics/types';
import type { Vehicle } from '../vehicles/types';
import type { Waypoint } from '../missions/autopilot';

const PLAN_BRAKE = 7;

/** Highest speed from which we can still slow to `cap` within `d` metres. */
function approach(cap: number, d: number): number {
  return Math.sqrt(cap * cap + 2 * PLAN_BRAKE * Math.max(0, d - 4));
}

export class RivalDriver {
  /** Index of the waypoint being steered for. */
  idx = 1;
  private stuckFor = 0;
  private reverseFor = 0;
  private avoidFor = 0;
  private wpTime = 0;
  /** Waypoint hops used (reported). */
  hops = 0;

  constructor(
    private readonly ctx: GameContext,
    readonly vehicle: Vehicle,
    private readonly line: readonly Waypoint[],
    /** Cruise (m/s) and corner-cap multiplier; `pace` is set every step by the session (rubber band). */
    private readonly cruise: number,
    private readonly corner: number,
  ) {}

  pace = 1;

  get done(): boolean {
    return this.idx >= this.line.length;
  }

  /** Waypoint the driver is heading for (null when finished). */
  get target(): V2 | null {
    return this.line[this.idx] ?? null;
  }

  /** Drive one fixed step. */
  drive(dt: number): void {
    const v = this.vehicle;
    if (v.destroyed || v.controller !== 'ai') return;
    const c = v.controls;
    if (this.done) {
      // Past the flag: roll to a stop.
      c.throttle = v.speed > 0.5 ? -0.6 : 0;
      c.steer = 0;
      c.handbrake = v.speed < 0.5;
      return;
    }
    const pos = v.pos;
    // Advance along the line: reached, or passed (beyond it along the way we came in).
    while (this.idx < this.line.length) {
      const cur = this.line[this.idx]!;
      const prv = this.line[this.idx - 1]!;
      const dx = cur.x - prv.x;
      const dz = cur.z - prv.z;
      const passed = dx * (pos.x - cur.x) + dz * (pos.z - cur.z) > 0;
      const r = cur.cap !== undefined && cur.cap < 10 ? 4.5 : 7;
      if (distance(pos, cur) < r || (passed && distance(pos, cur) < 22)) {
        this.idx++;
        this.wpTime = 0;
      } else break;
    }
    if (this.done) return;
    this.wpTime += dt;
    const wp = this.line[this.idx]!;
    const next = this.line[Math.min(this.idx + 1, this.line.length - 1)]!;
    // Wedged for good, and nobody watching: hop on (the player never sees a car teleport). Truly wedged
    // (a jam that won't clear) it hops anyway, as long as it isn't right under the player's nose.
    if ((this.wpTime > 9 && this.unseen()) || (this.wpTime > 16 && distance(this.ctx.services.get('player').pos, v.pos) > 30)) {
      this.hops++;
      this.wpTime = 0;
      const h = Math.atan2(next.x - wp.x, next.z - wp.z);
      if (this.ctx.physics.isShapeFree({ type: 'box', hx: v.spec.width / 2, hz: v.spec.length / 2 }, wp, h, Layer.VEHICLE | Layer.PLAYER)) {
        v.body.setPose({ x: wp.x, z: wp.z }, h);
        v.body.setVelocity({ x: 0, z: 0 }, 0);
        this.idx = Math.min(this.idx + 1, this.line.length - 1);
        return;
      }
    }
    let aimX = wp.x;
    let aimZ = wp.z;
    if (this.avoidFor > 0) {
      // Overtake on the crown: shift the aim point to the right of the line (we drive on the left).
      // right(h) = (-cos h, sin h); for a travel direction (dx, dz) that is (-dz, dx).
      this.avoidFor -= dt;
      const hx = wp.x - pos.x;
      const hz = wp.z - pos.z;
      const hl = Math.hypot(hx, hz) || 1;
      aimX += (-hz / hl) * 3.4;
      aimZ += (hx / hl) * 3.4;
    }
    const desired = Math.atan2(aimX - pos.x, aimZ - pos.z);
    const delta = angleDelta(v.heading, desired); // + = left
    const speed = v.telemetry.forwardSpeed;
    if (this.reverseFor > 0) {
      this.reverseFor -= dt;
      c.throttle = -0.6;
      c.steer = clamp(delta * 2, -1, 1);
      c.handbrake = false;
      if (v.body.sleeping) v.body.wake();
      return;
    }
    const turnAhead = Math.abs(angleDelta(desired, Math.atan2(next.x - wp.x, next.z - wp.z)));
    const top = Math.min(this.cruise * this.pace, v.spec.maxSpeed * 0.97);
    let want = clamp(top - Math.abs(delta) * 16 - turnAhead * 6 * clamp(1 - (distance(pos, wp) - 12) / 30, 0, 1), 5, top);
    const capMul = this.corner * this.pace;
    if (wp.cap !== undefined) want = Math.min(want, approach(wp.cap * capMul, distance(pos, wp)));
    if (next.cap !== undefined) want = Math.min(want, approach(next.cap * capMul, distance(pos, next)));
    // Traffic: look ahead along the nose (bent toward where we're steering). A slower car in the way is
    // passed on the crown; while passing only something right on the bumper makes us brake.
    const f = v.body.forward();
    const nose = { x: pos.x + f.x * (v.spec.length / 2 + 0.3), z: pos.z + f.z * (v.spec.length / 2 + 0.3) };
    const look = 6 + Math.max(0, speed) * 1.2;
    let hit: { distance: number; bodyId: number | null } | null = null;
    for (const fan of [0, 0.18, -0.18]) {
      const a = Math.atan2(f.x, f.z) + fan + clamp(delta, -0.5, 0.5) * 0.5;
      const r = this.ctx.physics.raycast(nose, { x: Math.sin(a), z: Math.cos(a) }, fan === 0 ? look : look * 0.6, {
        mask: Layer.VEHICLE | Layer.PLAYER,
        includeStatics: false,
        ignore: v.body,
      });
      if (r && (!hit || r.distance < hit.distance)) hit = r;
    }
    if (hit) {
      const other = hit.bodyId !== null ? this.ctx.services.get('vehicles').byBodyId(hit.bodyId) : undefined;
      const theirs = other ? Math.max(0, other.telemetry.forwardSpeed) : 0;
      const slower = theirs < Math.min(want, speed + 2) - 1.5;
      if (slower && this.avoidFor <= 0) this.avoidFor = 2.6;
      if (this.avoidFor > 0) {
        if (hit.distance < 2) want = Math.min(want, Math.max(1.5, theirs));
      } else want = Math.min(want, Math.max(theirs, (hit.distance - 3) * 1.2));
    }
    c.throttle = want < 0.5 && speed > 0.3 ? -1 : clamp((want - speed) * 0.4, -1, 1);
    c.steer = clamp(-delta * 2.4, -1, 1);
    c.handbrake = false;
    // Controls don't wake a resting body (it went to sleep on the grid during the countdown).
    if (c.throttle !== 0 && v.body.sleeping) v.body.wake();
    this.stuckFor = Math.abs(speed) < 0.7 ? this.stuckFor + dt : 0;
    if (this.stuckFor > 1.6) {
      this.stuckFor = 0;
      this.reverseFor = 1.1;
      this.avoidFor = 2.6;
    }
  }

  /** Far enough from the player (and out of their line of sight) that a hop goes unnoticed. */
  private unseen(): boolean {
    const p = this.ctx.services.get('player').pos;
    const v = this.vehicle.pos;
    const d = distance(p, v);
    return d > 70 || (d > 35 && !this.ctx.physics.lineOfSight(p, v));
  }
}
